import React, { useEffect, useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { parse } from 'query-string'
import cloneDeep from 'lodash/cloneDeep'
import useAxios from 'axios-hooks'
import { getAuthHeader } from 'utils/auth'
import { freeSearch } from 'utils/search'
import { navigate } from '@reach/router'
import { PageCrumbs, ContentContainer, notificationToast } from 'components'
import PermissionsBar from 'views/UserManagement/Permissions/PermissionsBar'
import ProjectsPermissions from 'views/UserManagement/Permissions/ProjectsPermissions'
import { Button } from 'antd'
import { useUser } from 'context/user-context'

const SEARCH_PERMISSIONS_MAPPING = {
  name: 'name'
}

const PERMS_OPTIONS = [
  { label: 'Run scan', name: 'Run scans' },
  { label: 'Manage scans', name: 'Manage scans' },
  { label: 'Export report', name: 'Export report' },
  { label: 'View dashboard', name: 'View dashboard' }
]

const PermissionsTop = styled.div({
  marginBottom: 20,
  display: 'grid',
  gridTemplateColumns: '1fr 180px'
})

export const PermissionsContext = React.createContext()

const openNotification = (message, errors) => {
  if (!errors) {
    notificationToast({
      message: 'Success',
      description: message,
      icontype: 'check'
    })
  } else {
    notificationToast({
      message: 'Error',
      description: `${message} ${errors.join()}`,
      icontype: 'close-circle'
    })
  }
}

function Permissions({ userId, location }) {
  const [permissions, setPermissions] = useState([])
  const [isAccountAdmin, setIsAccountAdmin] = useState(false)
  const { n: username } = parse(location.search)
  const { user_id: myUserId } = useUser()

  // permissions list
  const [{ data: permissionsData, loading: permissionsDataLoading, error: permissionsDataError }] = useAxios({
    url: `/account/users/${userId}/permissions`,
    method: 'GET',
    headers: getAuthHeader(),
    validateStatus: false
  })

  // save permissions
  const [{ data: permissionsSaved, loading: saving, error: saveError }, savePermissions] = useAxios(
    {
      url: `/account/users/${userId}/permissions`,
      method: 'PUT',
      headers: getAuthHeader(),
      validateStatus: false
    },
    { manual: true }
  )

  const handleSearch = term => {
    if (term && term.length) setPermissions(freeSearch(permissions, term, SEARCH_PERMISSIONS_MAPPING))
    else {
      setPermissions(permissionsData.projects)
    }
  }

  const save = () => {
    savePermissions({ data: { account_Admin: isAccountAdmin, projects: permissions } })
  }

  const PermissionsBarMemo = useMemo(
    () => (
      <PermissionsBar
        isAccountAdmin={isAccountAdmin}
        onAccountAdminChange={value => {
          let cloneData = cloneDeep(permissions)
          if (cloneData?.projects) {
            cloneData.projects = cloneData.projects.map(p => {
              return {
                ...p,
                project_admin: value
              }
            })
            setPermissions(cloneData.projects)
          }
          setIsAccountAdmin(value)
        }}
        onSearch={handleSearch}
        sameUser={userId==myUserId}
      />
    ),
    [isAccountAdmin]
  )

  const ProjectsPermissionsMemo = useMemo(
    () => (
      <ProjectsPermissions
        projects={permissions}
        onHiddenChange={(project, checked) => {
          let cloneProjects = cloneDeep(permissions)
          let projectIndex = cloneProjects.findIndex(p => p.id === project)
          cloneProjects[projectIndex].in_project = checked
          setPermissions(cloneProjects)
        }}
        onProjectAdminChange={(project, checked) => {
          let cloneProjects = cloneDeep(permissions)
          let projectIndex = cloneProjects.findIndex(p => p.id === project)
          cloneProjects[projectIndex].project_admin = checked
          setPermissions(cloneProjects)
        }}
        onPermissionsChange={(project, event) => {
          const { name, checked } = event.target
          let cloneProjects = cloneDeep(permissions)
          let projectIndex = cloneProjects.findIndex(p => p.id === project)
          let perms = cloneProjects[projectIndex].permissions
          cloneProjects[projectIndex].permissions = checked ? [...perms, name] : perms.filter(p => p !== name)
          setPermissions(cloneProjects)
        }}
      />
    ),
    [permissions]
  )

  /**
   * this effect set existing permissions
   */
  useEffect(() => {
    if (permissionsData) {
      setPermissions(permissionsData.projects)
      setIsAccountAdmin(permissionsData.account_admin)
    }
  }, [permissionsData])

  useEffect(() => {
    if (permissionsSaved) {
      if (permissionsSaved.Errors) {
        openNotification('Cannot save permissions.', permissionsSaved.Errors)
      } else {
        navigate("../");
        openNotification('Permissions saved successfully.')
      }
    }
    if (saveError) {
      const errorMessage = saveError?.response?.data?.Errors
      openNotification('Cannot save permissions.', errorMessage)
    }
  }, [permissionsSaved, saveError])

  return (
    <PermissionsContext.Provider value={{ permissionsOptions: PERMS_OPTIONS, isAccountAdmin }}>
      <PermissionsTop>
        <PageCrumbs
          crumbs={[{ name: 'Account Management', icon: 'team', path: '/account-management' }, { name: username }]}
        />
        <Button type="primary" id="newScanSettings" onClick={save} loading={saving}>
          SAVE CHANGES
        </Button>
      </PermissionsTop>
      <ContentContainer pad="none">
        {PermissionsBarMemo}
        {ProjectsPermissionsMemo}
      </ContentContainer>
    </PermissionsContext.Provider>
  )
}

export default Permissions
